import { DXG, useDataGrid } from 'components/devexpress/Grid/dx-grid';
import { Input } from 'components/uikit/inputs/Input';
import { useEffect, useMemo, useRef, useState } from 'react';
import formStyles from 'components/form/form.module.scss';
import gridStyles from 'components/devexpress/Grid/grid.module.scss';
import { QueryFactory } from 'services/api';
import {
  type IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/cjs/data-grid';
import { convertAbasTable, getQueryHeadField } from 'helpers/abas-helper';
import { Loading } from 'components/uikit/suspense/Loading';
import { exportDataGrid } from 'devextreme/pdf_exporter';
import { addHeader, fieldTitleCell } from 'helpers/pdf/pdf-helper';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import {
  EditorPreparingEvent,
  OptionChangedEvent,
} from 'devextreme/ui/data_grid';
import { errorToString } from 'helpers/error-helpers';
import clsx from 'clsx';
import { Pagination, PaginationProps } from 'components/uikit/table/Pagination';
import { MaxNumberOfShownPagesInPager } from '../articles/ArticlesPage';
import { Checkbox } from 'components/uikit/inputs/Checkbox';
import PrintIcon from '../../../assets/icons/print-icon.svg';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import {
  RequiredStringParam,
  StringParam,
  useQueryParams,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from 'helpers/pagination-helper.ts';
import { PlanChartsQuery } from 'services/api/api-client.ts';
import { Links } from '../../../application/constants/links.ts';
import { useNavigate } from 'react-router';
import { sortHelper } from '../../../helpers/sort-helper.ts';
import { DataGridRef } from 'devextreme-react/data-grid';

const pageSizes = [10, 20, 50];

export const PlanChartPage = () => {
  const navigate = useNavigate();
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [params, setParams] = useQueryParams({
    product: RequiredStringParam,
    warehouseGroup: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
  });

  useDataGrid();

  const planChartQuery = PlanChartsQuery.useGetQuery(
    {
      product: params.product ?? undefined,
      warehouseGroup: params.warehouseGroup,
      ...pagingSortingToBackendRequest(params),
    },
    {
      staleTime: 60 * 1000,
      throwOnError: false,
      placeholderData: (previousData) => previousData,
    },
  );

  useEffect(() => {
    if (planChartQuery.data)
      setNumberOfPages(
        Math.ceil(
          (planChartQuery.data?.content.data.meta.rowCount ?? 0) /
            params.perPage,
        ),
      );
  }, [params.perPage, planChartQuery?.data?.content.data.meta.rowCount]);

  useEffect(() => {
    params.page ??= 1;
    params.perPage ??= pageSizes[0];
    setParams(params);
  }, []);

  useEffect(() => {
    if (params.page) {
      const newValue = numberOfPages >= params.page ? params.page : 1;

      void setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  }, [params, numberOfPages]);

  function getHeadField(field: string) {
    return getQueryHeadField(planChartQuery, field);
  }

  const data = useMemo(() => {
    return convertAbasTable(planChartQuery.data?.content.data.table, {
      idField: 'zn',
    });
  }, [planChartQuery.data]);

  const onEditorPreparing = (e1: EditorPreparingEvent<any, number>) => {
    let dataField = e1.dataField!;
    if (dataField === 'tbestlief') {
      dataField = 'bstnr';
    } else if (dataField === 'tyrsichbestlief') {
      dataField = 'yrsichbestlief';
    }

    const id = e1.row?.data['_id'];
    const defaultOnValueChanged = e1.editorOptions.onValueChanged;
    e1.editorOptions.onValueChanged = async function (e: any) {
      defaultOnValueChanged(e);
      try {
        const patchDto: Record<string, any> = {
          [dataField]: e.value,
        };
        if (dataField === 'yrsichbestlief') {
          patchDto['ydsichbestdat'] = new Date().toISOString();
        }
        await QueryFactory.ArticlesClient.patch(id, patchDto);
      } catch (ex) {
        alert('Error ' + errorToString(ex));
      }
    };
  };

  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  const tableInstance = dataGridRef.current?.instance;

  const onExporting = async () => {
    const doc = new jsPDF({
      orientation: 'landscape',
    });

    addHeader(doc, 'Plan Chart');

    // Product Unit Warehouse
    // Product description Artikelnummer
    // Minimum stock level 0 Batch size 0 Gibts Beistellunge No
    // Beschaffungsfrist L1 0 Scheduling stock 0
    // Durchschnittsverbrauch der 0 Starttermin Tag -13*7 23.05.24 LUH 3 Monate 0,00
    // Durchschnittsver 0 Jahresverbrauch Starttermin LUH 12 Monate 0,00

    autoTable(doc, {
      alternateRowStyles: {
        fillColor: undefined,
      },
      styles: {
        textColor: 'black',
        cellPadding: 1,
      },
      //tableWidth: 'wrap',
      body: [
        [
          fieldTitleCell('Product'),
          getHeadField('kart'),
          fieldTitleCell('Unit'),
          '',
          fieldTitleCell('Warehouse'),
          '',
        ],
        [
          fieldTitleCell('Product description'),
          getHeadField('kartname'),
          '',
          '',
          fieldTitleCell('Artikelnummer'),
          getHeadField('kytartnrlief'),
        ],
        [
          fieldTitleCell('Minimum stock level'),
          '',
          fieldTitleCell('Batch size'),
          '',
          fieldTitleCell('Gibts Beistellunge No'),
          '',
        ],
        [
          fieldTitleCell('Beschaffungsfrist L1'),
          '',
          fieldTitleCell('Scheduling stock'),
          '',
          '',
          '',
        ],
        [
          fieldTitleCell('Durchschnittsverbrauch der'),
          '',
          fieldTitleCell('Starttermin Tag -13*7'),
          '',
          fieldTitleCell('LUH 3 Monate'),
          '',
        ],
        [
          fieldTitleCell('Durchschnittsver'),
          '',
          fieldTitleCell('Jahresverbrauch Starttermin'),
          '',
          fieldTitleCell('LUH 12 Monate'),
          '',
        ],
      ],
    });
    const lastY = (doc as any).lastAutoTable.finalY;

    const columnWidths: number[] = [];
    columnsConfig.forEach((value, index) => {
      if (value.disableExport) columnWidths[index] = 0;
    });
    columnWidths[columnsConfig.length] = 0;

    let rowIndex = 0;
    await exportDataGrid({
      columnWidths: columnWidths,
      jsPDFDocument: doc,
      component: tableInstance?.(),
      topLeft: { x: 0, y: lastY - 5 },
      indent: 0,
      onRowExporting(options) {
        rowIndex++;
        if (rowIndex > 1 && rowIndex % 2 === 1) {
          options.rowCells!.forEach((cell) => {
            cell.backgroundColor = '#e5e5e5';
          });
        }
      },
    });
    doc.save('Plan Chart.pdf');
  };

  const changePageSize = (value: number) => {
    tableInstance?.().pageSize(value);
    void setParams((prevParams) => ({
      ...prevParams,
      page: 1,
      perPage: value,
    }));
    void tableInstance?.().pageIndex(0);
  };

  const paginationArray = useMemo(() => {
    const result: number[] = [];

    let numberOfMinPage = Math.max(
      params.page - MaxNumberOfShownPagesInPager,
      1,
    );
    let numberOfMaxPage = Math.min(
      numberOfMinPage + MaxNumberOfShownPagesInPager - 1,
      numberOfPages,
    );

    if (params.page >= numberOfMaxPage) {
      numberOfMaxPage = Math.min(numberOfPages, params.page + 1);
      if (numberOfMaxPage > MaxNumberOfShownPagesInPager) {
        numberOfMinPage = numberOfMaxPage - MaxNumberOfShownPagesInPager + 1;
      }
    }
    for (let i = numberOfMinPage; i <= numberOfMaxPage; i++) {
      result.push(i);
    }

    return result;
  }, [numberOfPages, params.page]);

  const goToFirstPage = () => {
    if (params.page > 1) {
      void setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
    }
  };

  const goToPreviousPage = () => {
    if (params.page - 1 >= 1) {
      const newValue = params.page - 1;

      void setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  };

  const goToNextPage = () => {
    if (params.page + 1 <= numberOfPages) {
      const newValue = params.page + 1;

      void setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  };

  const goToLastPage = () => {
    if (params.page < numberOfPages) {
      const newValue = numberOfPages;

      void setParams((prevParams) => ({
        ...prevParams,
        page: newValue,
      }));
    }
  };

  const goToCertainPage = (value: number) => {
    void setParams((prevParams) => ({
      ...prevParams,
      page: value,
    }));
  };

  const paginationProps: PaginationProps = {
    pageSize: params.perPage,
    pageSizes,
    currentPage: params.page,
    changePageSize,
    goToCertainPage,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    numberOfPages,
    paginationArray,
    numberOfRows: data?.length ?? 0,
  };
  const handleOptionChange = (e: OptionChangedEvent) => {
    const sortData = sortHelper(e, params.desc, params.sortBy);

    if (sortData) {
      setParams((prevParams) => ({
        ...prevParams,
        sortBy: sortData.dataField,
        desc: sortData.isDesc,
      }));
    }
  };

  useEffect(() => {
    tableInstance?.().pageSize(params.perPage);
  }, [params.perPage]);

  useEffect(() => {
    const goBack = () => {
      void navigate(Links.Authorized.Articles.route);
    };

    addEventListener('popstate', goBack);

    return () => {
      removeEventListener('popstate', goBack);
    };
  }, []);

  return (
    <div>
      <Breadcrumbs>
        <Button icon={PrintIcon} title={'Print'} onClick={onExporting} />
      </Breadcrumbs>
      <Loading loading={planChartQuery.isFetching}>
        <div
          className={clsx(
            formStyles.columnContainer,
            formStyles.inputContainer,
          )}
        >
          <div
            className={clsx(formStyles.rowContainer, formStyles.spaceBetween)}
          >
            <div
              style={{ columnGap: 24 }}
              className={clsx(
                formStyles.rowContainer,
                formStyles.containerWidth_610,
              )}
            >
              <Input
                className={formStyles.inputWidth_220}
                disabled={true}
                value={getHeadField('kart')}
                leftHelperText={'Part No'}
                isSmallInput={true}
              />
              <Input
                className={formStyles.inputWidth_180}
                disabled={true}
                value={getHeadField('lein')}
                leftHelperText={'Stock unit'}
                isSmallInput={true}
              />
            </div>
            <Input
              className={formStyles.inputWidth_220}
              disabled={true}
              value={getHeadField('kbezlgruppe')}
              leftHelperText={'Warehouse group'}
              isSmallInput={true}
            />
          </div>
          <div
            className={clsx(formStyles.rowContainer, formStyles.spaceBetween)}
          >
            <div
              className={clsx(
                formStyles.rowContainer,
                formStyles.containerWidth_610,
              )}
            >
              <Input
                className={formStyles.inputWidth_500}
                disabled={true}
                value={getHeadField('kartname')}
                leftHelperText={'Description'}
                isSmallInput={true}
              />
            </div>
            <Input
              className={formStyles.inputWidth_220}
              disabled={true}
              value={getHeadField('kytartnrlief')}
              leftHelperText={'Article-No. Supplier'}
              isSmallInput={true}
            />
          </div>
          <div
            className={clsx(formStyles.rowContainer, formStyles.spaceBetween)}
          >
            <div
              style={{ columnGap: 40 }}
              className={clsx(
                formStyles.rowContainer,
                formStyles.spaceBetween,
                formStyles.containerWidth_610,
              )}
            >
              <Input
                className={formStyles.inputWidth_220}
                disabled={true}
                value={getHeadField('mind')}
                leftHelperText={'Min. stock level'}
                isSmallInput={true}
              />
              <Input
                className={formStyles.inputWidth_180}
                disabled={true}
                value={getHeadField('losgr')}
                leftHelperText={'Lot size'}
                isSmallInput={true}
              />
            </div>
            <Checkbox
              containerClassName={formStyles.marginRightCheckboxContainer}
              helperText={'Checkbox text'}
              disabled={true}
            />
          </div>
          <div
            className={clsx(
              formStyles.rowContainer,
              formStyles.spaceBetween,
              formStyles.containerWidth_610,
              formStyles.flexStart,
            )}
          >
            <Input
              className={formStyles.inputWidth_180}
              disabled={true}
              value={getHeadField('efrist')}
              leftHelperText={'BFrist'}
              rightHelperText={'workdays'}
              isSmallInput={true}
            />
          </div>
          <div
            className={clsx(formStyles.rowContainer, formStyles.spaceBetween)}
          >
            <div
              style={{ columnGap: 56 }}
              className={clsx(
                formStyles.rowContainer,
                formStyles.containerWidth_610,
              )}
            >
              <Input
                className={formStyles.inputWidth_220}
                disabled={true}
                value={getHeadField('dverbr')}
                leftHelperText={'Weekly run rate'}
                isSmallInput={true}
              />
              <Input
                className={formStyles.inputWidth_180}
                disabled={true}
                value={getHeadField('verbrdat')}
                leftHelperText={'since'}
                isSmallInput={true}
              />
            </div>
            <Input
              className={formStyles.inputWidth_220}
              disabled={true}
              value={getHeadField('yluh3')}
              leftHelperText={'Inventory turnover ratio 3 months'}
              isSmallInput={true}
            />
          </div>
          <div
            className={clsx(formStyles.rowContainer, formStyles.spaceBetween)}
          >
            <div
              style={{ columnGap: 56 }}
              className={clsx(
                formStyles.rowContainer,
                formStyles.containerWidth_610,
              )}
            >
              <Input
                className={formStyles.inputWidth_220}
                disabled={true}
                value={getHeadField('y12dverb')}
                leftHelperText={'Weekly run rate'}
                isSmallInput={true}
              />
              <Input
                className={formStyles.inputWidth_180}
                disabled={true}
                value={getHeadField('yverb12dat')}
                leftHelperText={'since'}
                isSmallInput={true}
              />
            </div>
            <Input
              className={formStyles.inputWidth_220}
              disabled={true}
              value={getHeadField('yluh12')}
              leftHelperText={'Inventory turnover ratio 12 months'}
              isSmallInput={true}
            />
          </div>
        </div>
        <div style={{ marginTop: 24 }}>
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={data}
            className={clsx(gridStyles.editorOutline, formStyles.table)}
            onEditorPreparing={onEditorPreparing}
            onOptionChanged={handleOptionChange}
            sorting={{ mode: 'single' }}
          >
            <Scrolling columnRenderingMode={'virtual'} />
            <DXG.Column
              width={40}
              cssClass={clsx(formStyles.rowIndex, formStyles.textCellPadding)}
              cellRender={(cell) => {
                return <div>{cell.row.loadIndex + 1}</div>;
              }}
            />
            {columnsConfig.map((x, index) => {
              const sortByField = params.sortBy || 'ddispo';
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={index}
                  sortOrder={
                    x.dataField === sortByField
                      ? params.desc
                        ? 'desc'
                        : 'asc'
                      : undefined
                  }
                  {...x}
                  showEditorAlways={x.allowEditing}
                />
              );
            })}
            <Pager visible={false} />
            <DXG.Editing
              mode="cell"
              allowUpdating={true}
              allowAdding={false}
              allowDeleting={false}
            />
          </DXG.DataGrid>
          {data?.length && <Pagination {...paginationProps} />}
        </div>
      </Loading>
    </div>
  );
};

const columnsConfig: (IColumnProps & { disableExport?: boolean })[] = [
  {
    dataField: 'ddispo',
    caption: 'H',
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 80,
  },
  {
    dataField: 'tterm',
    caption: 'ActDate',
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 150,
  },
  {
    dataField: 'twterm',
    caption: 'TargDate',
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 150,
  },
  {
    dataField: 'fterm',
    caption: 'EarlDate',
    disableExport: true,
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 150,
  },
  {
    dataField: 'losgr',
    caption: 'Rec',
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 100,
  },
  {
    dataField: 'abgang',
    caption: 'Issue',
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 120,
  },
  {
    dataField: 'art',
    caption: 'Module',
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 120,
  },
  {
    dataField: 'verw',
    caption: 'Use',
    disableExport: true,
    dataType: 'boolean',
    allowEditing: true,
    cssClass: clsx(formStyles.textCellPadding, formStyles.checkbox),
    minWidth: 120,
  },
  {
    dataField: 'vart',
    caption: 'AC',
    disableExport: true,
    allowEditing: false,
    cssClass: formStyles.textCellPadding,
    minWidth: 200,
  },
  // { dataField: '', caption: 'Transaction' },
  // { dataField: '', caption: 'Firm' },
  // { dataField: '', caption: 'C' },
  // { dataField: '', caption: '' },
];
